/**
 * 크레딧 충전 완료 페이지 — 토스 successUrl 도착점.
 *
 *  URL 파라미터: paymentKey, orderId, amount
 *  → BE confirm 호출 → 크레딧 적립 → 성공 화면.
 *
 *  ⚠ 기존 customer/layout/payment/success 와 분리.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { LayoutHeader } from "@/Header";
import { walletChargeApi, type BuyCreditConfirmResponse } from "~/services/business/walletCharge";
import { CREDIT } from "%/credit";
import { openMungAppDeepLink, wasOpenedFromMungApp } from "%/appDeepLink";

export const Route = createFileRoute("/business/layout/user-credit-wallet-charge-success")({
  component: ChargeSuccess,
});

function ChargeSuccess() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState<"processing" | "done" | "error">("processing");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [result, setResult] = useState<BuyCreditConfirmResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const hasCalledRef = useRef(false);

  useEffect(() => {
    if (hasCalledRef.current) return;
    hasCalledRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const paymentKey = params.get("paymentKey");
    const orderId = params.get("orderId");
    const amountRaw = params.get("amount");

    if (!paymentKey || !orderId || !amountRaw) {
      setErrorMsg("결제 정보가 누락되었습니다.");
      setState("error");
      return;
    }
    const amount = parseInt(amountRaw, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      setErrorMsg("결제 금액이 유효하지 않습니다.");
      setState("error");
      return;
    }

    // confirm 호출 — 최대 3회 자동 재시도 (지수 백오프)
    //   no healthy upstream / 5xx / 네트워크 오류는 일시적일 수 있어 짧게 재시도
    const retryDelays = [500, 1500, 3000]; // ms
    const attemptConfirm = async (attempt: number): Promise<void> => {
      try {
        const r = await walletChargeApi.confirm({ paymentKey, orderId, amount });
        setResult(r);
        setState("done");
      } catch (e: any) {
        const status: number | undefined = e?.response?.status;
        const body: any = e?.response?.data;
        const bodyText = typeof body === "string" ? body : "";
        const isGatewayError =
          (status != null && status >= 500) ||
          bodyText.toLowerCase().includes("no healthy upstream") ||
          e?.code === "ERR_NETWORK" ||
          e?.message?.toLowerCase?.().includes("network");

        if (isGatewayError && attempt < retryDelays.length) {
          await new Promise((res) => setTimeout(res, retryDelays[attempt]));
          return attemptConfirm(attempt + 1);
        }

        // 사용자 친화 메시지
        let msg: string;
        if (isGatewayError) {
          msg =
            "서버 연결이 일시적으로 끊겼어요 (gateway). 결제는 정상 처리됐을 수 있어요. " +
            "잠시 후 크레딧에서 잔액을 확인하거나 고객센터로 문의해주세요.";
        } else if (status === 401 || status === 403) {
          msg = "인증이 만료됐어요. 다시 로그인 후 크레딧에서 잔액을 확인해주세요.";
        } else {
          msg =
            (body && body.message) ||
            (e instanceof Error ? e.message : String(e)) ||
            "알 수 없는 오류";
        }
        console.error("[CHARGE-SUCCESS] confirm 실패:", { status, body, error: e });
        setErrorMsg(msg);
        setState("error");
      }
    };
    void attemptConfirm(0);
  }, []);

  const fmt = (n: number) => n.toLocaleString();

  /** 가상계좌 — 계좌만 발급된 상태. 입금 전이라 아직 충전되지 않았다. */
  const isWaitingDeposit = result?.paymentStatus === "WAITING_DEPOSIT";

  /** "2026-09-07T10:18:04" → "9월 7일(월) 오전 10:18" */
  const fmtDue = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const days = ["일", "월", "화", "수", "목", "금", "토"];
    const h = d.getHours();
    const ampm = h < 12 ? "오전" : "오후";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${d.getMonth() + 1}월 ${d.getDate()}일(${days[d.getDay()]}) ${ampm} ${h12}:${mm}`;
  };

  const copyAccount = async () => {
    const num = result?.virtualAccountNumber;
    if (!num) return;
    try {
      await navigator.clipboard.writeText(num);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 앱뷰/구형 브라우저에서 clipboard API 가 막힌 경우 — 조용히 무시하고
      // 사용자가 직접 길게 눌러 복사할 수 있게 둔다.
      setCopied(false);
    }
  };

  const goToWallet = (paymentReturn: "success" | "error") => {
    if (wasOpenedFromMungApp()) {
      // 앱 복귀 — 딥링크 라우터가 CREDIT_PAYMENT_RETURN 수신 시 캐시 무효화
      openMungAppDeepLink("/business/layout/user-credit-wallet", {
        query: {
          paymentReturn,
          ts: Date.now(),
        },
        type: "CREDIT_PAYMENT_RETURN",
      });
      return;
    }

    // 웹(동일 SPA) — 크레딧 이동 전 직접 캐시 무효화 (잔액/사용내역 최신화)
    void queryClient.invalidateQueries({ queryKey: ["business-credit"] });
    navigate({ to: "/business/layout/user-credit-wallet", replace: true });
  };

  return (
    <>
      <LayoutHeader title="결제 결과" hasBackBtn hasFootPrint={false} />
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5] px-4 py-8">
        {state === "processing" && (
          <div className="py-12 text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-[#FFE3B3] border-t-[#FF9934]" />
            <p className="mt-4 text-sm text-gray-600">결제를 확인하고 있어요...</p>
          </div>
        )}

        {/* 가상계좌 — 계좌 발급 완료, 입금 대기. 아직 충전된 게 아니라는 점을 분명히 알린다. */}
        {state === "done" && result && isWaitingDeposit && (
          <div className="space-y-4">
            <div className="rounded-lg border border-[#FFE3B3] bg-[#FFF7EC] p-5 text-center">
              <div className="text-3xl">🏦</div>
              <h2 className="mt-2 text-lg font-bold text-[#B35F00]">입금 대기 중</h2>
              <p className="mt-1 text-sm text-[#8A5A1E]">
                아래 계좌로 입금하시면 {CREDIT.emoji} <strong>{fmt(result.creditAmount)}</strong> 크레딧이
                자동으로 충전돼요
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="text-xs text-gray-500">입금 계좌</div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-base font-bold text-gray-900">
                    {result.virtualAccountBankName ?? "-"}
                  </div>
                  <div className="mt-0.5 break-all font-mono text-lg font-bold tracking-tight text-gray-900">
                    {result.virtualAccountNumber ?? "-"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={copyAccount}
                  className="shrink-0 rounded-md border border-[#FF9934] px-3 py-2 text-xs font-semibold text-[#FF9934]"
                >
                  {copied ? "복사됨" : "복사"}
                </button>
              </div>

              <div className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">예금주</span>
                  <span className="font-medium">{result.virtualAccountHolder ?? "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">입금 금액</span>
                  <span className="font-bold">₩{fmt(result.payAmount)}</span>
                </div>
                {fmtDue(result.dtDepositDue) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">입금 기한</span>
                    <span className="font-medium text-red-600">{fmtDue(result.dtDepositDue)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs text-gray-500">
                  <span>주문번호</span>
                  <span className="font-mono">{result.orderId}</span>
                </div>
              </div>
            </div>

            <ul className="space-y-1 rounded-lg bg-gray-50 p-4 text-xs leading-relaxed text-gray-600">
              <li>· 입금 금액이 정확히 일치해야 자동으로 충전돼요.</li>
              <li>· 입금 확인까지 보통 1~2분 걸려요. 창을 닫아도 괜찮아요.</li>
              <li>· 입금 기한이 지나면 계좌가 만료되며, 다시 충전해 주셔야 해요.</li>
              <li>· 이 계좌는 이번 결제에만 쓰이는 일회용 계좌예요.</li>
              <li>· 가상계좌 결제에 대한 취소는 관리자에게 직접 연락 바랍니다.</li>
            </ul>

            <button
              type="button"
              onClick={() => goToWallet("success")}
              className="block w-full rounded-md bg-[#FF9934] py-3 text-base font-semibold text-white"
            >
              크레딧으로 돌아가기
            </button>
          </div>
        )}

        {state === "done" && result && !isWaitingDeposit && (
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-center">
              <div className="text-3xl">✅</div>
              <h2 className="mt-2 text-lg font-bold text-emerald-800">충전 완료</h2>
              <p className="mt-1 text-sm text-emerald-700">
                {CREDIT.emoji} <strong>{fmt(result.creditAmount)}</strong> 크레딧이 충전됐어요
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">결제 금액</span>
                <span className="font-medium">₩{fmt(result.payAmount)}</span>
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-gray-600">충전 후 총 잔액</span>
                <span className="font-bold text-lg">
                  {CREDIT.emoji} {fmt(result.balanceAfter)}
                </span>
              </div>
              {result.paymentMethod && (
                <div className="mt-1 flex justify-between text-xs text-gray-500">
                  <span>결제 수단</span>
                  <span>{result.paymentMethod}</span>
                </div>
              )}
              <div className="mt-1 flex justify-between text-xs text-gray-500">
                <span>주문번호</span>
                <span className="font-mono">{result.orderId}</span>
              </div>
            </div>

            {result.pgReceiptUrl && (
              <a
                href={result.pgReceiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full rounded-md border border-gray-300 py-3 text-center text-sm font-medium text-gray-700"
              >
                영수증 보기
              </a>
            )}

            <button
              type="button"
              onClick={() => goToWallet("success")}
              className="block w-full rounded-md bg-[#FF9934] py-3 text-base font-semibold text-white"
            >
              크레딧으로 돌아가기
            </button>
          </div>
        )}

        {state === "error" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-center">
              <div className="text-3xl">⚠</div>
              <h2 className="mt-2 text-lg font-bold text-red-800">결제 확인 실패</h2>
              <p className="mt-1 text-sm text-red-700">{errorMsg}</p>
              <p className="mt-2 text-xs text-red-600">
                결제는 정상 처리됐을 수 있어요. 잔액을 확인하거나 고객센터로 문의해주세요.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => navigate({ to: "/business/layout/user-credit-wallet-charge" })}
                className="flex-1 rounded-md border border-gray-300 py-3 text-sm font-medium text-gray-700"
              >
                다시 시도
              </button>
              <button
                type="button"
                onClick={() => goToWallet("error")}
                className="flex-1 rounded-md bg-[#FF9934] py-3 text-sm font-semibold text-white"
              >
                크레딧으로
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
